import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, CheckCircle } from 'lucide-react';
import { useCookiePreferences, CookiePreferences } from '../hooks/useCookiePreferences';

const CookieSettingsButton: React.FC = () => {
  const [showSettings, setShowSettings] = useState(false);
  const { preferences, savePreferences, acceptAll, acceptNecessary, resetPreferences } = useCookiePreferences();

  const handlePreferenceChange = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // No se puede desactivar
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };
    savePreferences(newPreferences);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setShowSettings(false);
  };

  const handleResetPreferences = () => {
    resetPreferences();
    setShowSettings(false);
  };

  return (
    <>
      {/* Botón de configuración */}
      <button
        onClick={() => setShowSettings(true)}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        title="Configurar cookies"
      >
        <Settings className="w-4 h-4" />
        Configurar cookies
      </button>

      {/* Modal de configuración */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Configuración de cookies
                  </h2>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="Cerrar configuración"
                    aria-label="Cerrar configuración de cookies"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <p className="text-gray-600">
                    Puede gestionar sus preferencias de cookies en cualquier momento. 
                    Para obtener más información, consulte nuestra{' '}
                    <a 
                      href="/cookies" 
                      className="text-primary-600 hover:text-primary-800 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      política de cookies
                    </a>{' '}
                    y nuestra{' '}
                    <a 
                      href="/privacidad" 
                      className="text-primary-600 hover:text-primary-800 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      política de privacidad
                    </a>.
                  </p>

                  <div className="space-y-4">
                    {/* Cookies necesarias */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">Cookies necesarias</h3>
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Siempre activas
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar.
                        </p>
                      </div>
                      <div className="flex items-center ml-4">
                        <input
                          type="checkbox"
                          checked={preferences.necessary}
                          disabled
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                          aria-label="Cookies necesarias (siempre activas)"
                        />
                      </div>
                    </div>

                    {/* Cookies analíticas */}
                    <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">Cookies analíticas</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Nos ayudan a entender cómo interactúan los visitantes con el sitio web recopilando y reportando información de forma anónima.
                        </p>
                        <p className="text-xs text-gray-500">
                          Incluye Google Analytics y herramientas similares.
                        </p>
                      </div>
                      <div className="flex items-center ml-4">
                        <input
                          type="checkbox"
                          checked={preferences.analytics}
                          onChange={() => handlePreferenceChange('analytics')}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                          aria-label="Activar cookies analíticas"
                        />
                      </div>
                    </div>

                    {/* Cookies de marketing */}
                    <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">Cookies de marketing</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Se utilizan para rastrear a los visitantes en los sitios web con el fin de mostrar anuncios relevantes y atractivos.
                        </p>
                        <p className="text-xs text-gray-500">
                          Incluye cookies de redes sociales y publicidad.
                        </p>
                      </div>
                      <div className="flex items-center ml-4">
                        <input
                          type="checkbox"
                          checked={preferences.marketing}
                          onChange={() => handlePreferenceChange('marketing')}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                          aria-label="Activar cookies de marketing"
                        />
                      </div>
                    </div>

                    {/* Cookies de preferencias */}
                    <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">Cookies de preferencias</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Permiten que el sitio web recuerde información que cambia la forma en que se comporta o se ve.
                        </p>
                        <p className="text-xs text-gray-500">
                          Incluye preferencias de idioma y configuración regional.
                        </p>
                      </div>
                      <div className="flex items-center ml-4">
                        <input
                          type="checkbox"
                          checked={preferences.preferences}
                          onChange={() => handlePreferenceChange('preferences')}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                          aria-label="Activar cookies de preferencias"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                    <button
                      onClick={acceptAll}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aceptar todas
                    </button>
                    <button
                      onClick={handleSavePreferences}
                      className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                    >
                      Guardar preferencias
                    </button>
                    <button
                      onClick={handleResetPreferences}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Restablecer
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieSettingsButton; 